import { useCallback, useContext } from "react";
import { AnalyticsEventPayload } from "@swiggy-private/analytics/dist/interfaces";
import type { TContext } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import {
    ResponseContext,
    ScreenNames,
    useDigitalMenuConfig,
    TableContext,
} from "@swiggy-private/portal-dineout-context";
import { updateAnalyticsData } from "../utils";

const useAnalytics = ({
    screenName,
    defaultImpressionAnalytics,
    defaultClickAnalytics,
}: {
    screenName: ScreenNames;
    defaultImpressionAnalytics?: (impressionObject: TContext) => void;
    defaultClickAnalytics?: (clickObject: TContext) => void;
}) => {
    const { state } = useContext(ResponseContext);
    const tableContext = useContext(TableContext);
    const { config } = useDigitalMenuConfig();
    const defaultScreenName = screenName || ScreenNames.MENU;

    const impressionAnalytics = useCallback(
        (analyticsData: Partial<AnalyticsEventPayload>) => {
            const updatedAnalyticsData = updateAnalyticsData({
                analyticsData,
                tableContext,
                screenName: defaultScreenName,
                state,
                eventType: "impression",
                config,
            });
            defaultImpressionAnalytics?.(updatedAnalyticsData);
        },
        [config, defaultImpressionAnalytics, defaultScreenName, state, tableContext]
    );

    const clickAnalytics = useCallback(
        (analyticsData: Partial<AnalyticsEventPayload>) => {
            const updatedAnalyticsData = updateAnalyticsData({
                analyticsData,
                tableContext,
                screenName: defaultScreenName,
                state,
                config,
                eventType: "click",
            });
            defaultClickAnalytics?.(updatedAnalyticsData);
        },
        [config, defaultClickAnalytics, defaultScreenName, state, tableContext]
    );

    return { impressionAnalytics, clickAnalytics };
};

export default useAnalytics;
